import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight, X, Calendar, Clock, DollarSign, Users } from 'lucide-react';
import { ClientUtilization, LTSSService, Program } from '../../types/ltss';
import { formatCurrency } from '../../utils/timeUtils';

interface ClientTableProps {
  clients: ClientUtilization[];
  services?: LTSSService[];
}


const PROGRAM_LABELS: Record<Program, string> = {
  CFC: 'Community First Choice',
  CO: 'Community Options',
  CPAS: 'Community Personal Assistance Services',
  EPSDT: 'Early Periodic Screening Diagnostic Treatment',
  REM: 'Rare & Expensive Case Management',
};

const PROGRAM_COLORS: Record<Program, string> = {
  CFC: 'from-blue-500 to-blue-600',
  CO: 'from-green-500 to-green-600',
  CPAS: 'from-purple-500 to-purple-600',
  EPSDT: 'from-orange-500 to-orange-600',
  REM: 'from-pink-500 to-pink-600',
};

export function ClientTable({ clients, services = [] }: ClientTableProps) {
  const [selectedClient, setSelectedClient] = useState<{ clientName: string; week: string; revenue: number } | null>(null);
  const [expandedPrograms, setExpandedPrograms] = useState<Set<Program>>(new Set<Program>(['CFC', 'CO', 'CPAS', 'EPSDT', 'REM']));
  // Initialize with all clients expanded by default
  const [expandedClients, setExpandedClients] = useState<Set<string>>(() => {
    const allClientKeys = new Set<string>();
    clients.forEach(c => {
      allClientKeys.add(`${c.program}-${c.clientName}`);
    });
    return allClientKeys;
  });

  // Get services for a specific client and week
  const getClientServices = (clientName: string, week: string) => {
    return services.filter(s => 
      s.clientName === clientName && 
      s.week === week && 
      s.amountPaid && 
      s.amountPaid > 0
    );
  };

  const toggleProgram = (program: Program) => {
    const newExpanded = new Set(expandedPrograms);
    if (newExpanded.has(program)) {
      newExpanded.delete(program);
    } else {
      newExpanded.add(program);
    }
    setExpandedPrograms(newExpanded);
  };

  const toggleClient = (clientKey: string) => {
    const newExpanded = new Set(expandedClients);
    if (newExpanded.has(clientKey)) {
      newExpanded.delete(clientKey);
    } else {
      newExpanded.add(clientKey);
    }
    setExpandedClients(newExpanded);
  };

  // Group clients by program, then by client name
  const groupedByProgram = useMemo(() => {
    const programs: Program[] = ['CFC', 'CO', 'CPAS', 'EPSDT', 'REM'];
    const grouped: Record<Program, { 
      clients: Record<string, ClientUtilization[]>;
      totals: { authorized: number; used: number; revenue: number; count: number };
    }> = {} as any;

    programs.forEach(program => {
      grouped[program] = { clients: {}, totals: { authorized: 0, used: 0, revenue: 0, count: 0 } };
    });

    clients.forEach(c => {
      if (!grouped[c.program]) return;
      
      if (!grouped[c.program].clients[c.clientName]) {
        grouped[c.program].clients[c.clientName] = [];
      }
      grouped[c.program].clients[c.clientName].push(c);
      grouped[c.program].totals.authorized += c.authorizedHours;
      grouped[c.program].totals.used += c.usedHours;
      grouped[c.program].totals.revenue += c.revenue;
      grouped[c.program].totals.count++;
    });

    return grouped;
  }, [clients]);

  const programs: Program[] = ['CFC', 'CO', 'CPAS', 'EPSDT', 'REM'];

  return (
    <div className="space-y-4">
      {/* Section Header */}
      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
        <p className="text-slate-300 text-sm">
          Client utilization grouped by program. Click a program to expand/collapse, then click a client to see their weekly breakdown. 
          <span className="text-yellow-400"> Yellow</span> indicates underutilization, 
          <span className="text-green-400"> green</span> is on target, and 
          <span className="text-red-400"> red</span> means over-authorized hours.
        </p>
      </div>

      {/* Programs */}
      {programs.map(program => {
        const programData = groupedByProgram[program];
        const clientNames = Object.keys(programData.clients);
        const clientCount = clientNames.length;
        const utilizationRate = programData.totals.authorized > 0 
          ? (programData.totals.used / programData.totals.authorized) * 100 
          : 0;

        if (clientCount === 0) return null;

        return (
          <div key={program} className="card overflow-hidden">
            {/* Program Header */}
            <button
              onClick={() => toggleProgram(program)}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-700/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${
                  expandedPrograms.has(program) ? 'rotate-90' : ''
                }`} />
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${PROGRAM_COLORS[program]} flex items-center justify-center`}>
                  <span className="text-white font-bold text-sm">{program}</span>
                </div>
                <div className="text-left">
                  <h3 className="text-base font-semibold text-white">{PROGRAM_LABELS[program]}</h3>
                  <p className="text-xs text-slate-400 flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {clientCount} client{clientCount !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center text-base">
                <div className="text-right w-28">
                  <p className="text-slate-400 text-sm">Authorized</p>
                  <p className="text-slate-200 font-semibold text-lg">{programData.totals.authorized.toFixed(1)} hrs</p>
                </div>
                <div className="text-right w-28">
                  <p className="text-slate-400 text-sm">Used</p>
                  <p className="text-slate-200 font-semibold text-lg">{programData.totals.used.toFixed(1)} hrs</p>
                </div>
                <div className="text-right w-24">
                  <p className="text-slate-400 text-sm">Utilization</p>
                  <p className={`font-semibold text-lg ${
                    utilizationRate >= 90 ? 'text-green-400' : 
                    utilizationRate >= 70 ? 'text-yellow-400' : 'text-red-400'
                  }`}>{Math.round(utilizationRate)}%</p>
                </div>
                <div className="text-right w-28">
                  <p className="text-slate-400 text-sm">Revenue</p>
                  <p className="text-green-400 font-semibold text-lg">{formatCurrency(programData.totals.revenue)}</p>
                </div>
              </div>
            </button>

            {/* Clients List */}
            {expandedPrograms.has(program) && (
              <div className="border-t border-slate-700">
                {clientNames.map(clientName => {
                  const clientRecords = programData.clients[clientName];
                  const clientKey = `${program}-${clientName}`;
                  const isExpanded = expandedClients.has(clientKey);
                  
                  // Aggregate client totals
                  const clientTotals = clientRecords.reduce((acc, c) => ({
                    authorized: acc.authorized + c.authorizedHours,
                    used: acc.used + c.usedHours,
                    revenue: acc.revenue + c.revenue,
                  }), { authorized: 0, used: 0, revenue: 0 });
                  
                  const clientUtilization = clientTotals.authorized > 0 
                    ? (clientTotals.used / clientTotals.authorized) * 100 
                    : 0;

                  return (
                    <div key={clientKey} className="border-b border-slate-700/50 last:border-b-0">
                      {/* Client Row */}
                      <button
                        onClick={() => toggleClient(clientKey)}
                        className="w-full px-4 py-3 pl-12 flex items-center justify-between hover:bg-slate-800/50 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <ChevronRight className={`w-4 h-4 text-slate-500 transition-transform ${
                            isExpanded ? 'rotate-90' : ''
                          }`} />
                          <div className="text-left">
                            <p className="text-sm font-medium text-slate-200">{clientName}</p>
                            <p className="text-xs text-slate-500">{clientRecords.length} week{clientRecords.length !== 1 ? 's' : ''}</p>
                          </div>
                        </div>
                        <div className="flex items-center text-sm">
                          <div className="text-right w-28">
                            <p className="text-slate-300">{clientTotals.authorized.toFixed(1)} hrs</p>
                          </div>
                          <div className="text-right w-28">
                            <p className="text-slate-300">{clientTotals.used.toFixed(1)} hrs</p>
                          </div>
                          <div className="text-right w-24">
                            <span className={`font-medium ${
                              clientUtilization >= 90 ? 'text-green-400' : 
                              clientUtilization >= 70 ? 'text-yellow-400' : 'text-red-400'
                            }`}>{Math.round(clientUtilization)}%</span>
                          </div>
                          <div className="text-right w-28">
                            <span className="text-green-400 font-medium">{formatCurrency(clientTotals.revenue)}</span>
                          </div>
                        </div>
                      </button>

                      {/* Weekly Breakdown */}
                      {isExpanded && (
                        <div className="bg-slate-800/30 px-4 py-2 pl-16">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-xs text-slate-500">
                                <th className="text-left py-1 font-medium">Week</th>
                                <th className="text-right py-1 font-medium">Authorized</th>
                                <th className="text-right py-1 font-medium">Used</th>
                                <th className="text-right py-1 font-medium">Utilization</th>
                                <th className="text-right py-1 font-medium">Variance</th>
                                <th className="text-right py-1 font-medium">Revenue</th>
                              </tr>
                            </thead>
                            <tbody>
                              {clientRecords.map((c, idx) => (
                                <tr key={idx} className="border-t border-slate-700/30">
                                  <td className="py-2 text-slate-400">{c.week}</td>
                                  <td className="py-2 text-right text-slate-300">{c.authorizedHours.toFixed(1)} hrs</td>
                                  <td className="py-2 text-right text-slate-300">{c.usedHours.toFixed(1)} hrs</td>
                                  <td className="py-2 text-right">
                                    <span className={`${
                                      c.utilizationRate >= 90 ? 'text-green-400' : 
                                      c.utilizationRate >= 70 ? 'text-yellow-400' : 'text-red-400'
                                    }`}>{Math.round(c.utilizationRate)}%</span>
                                  </td>
                                  <td className="py-2 text-right">
                                    <span className={`${
                                      c.variance > 0 ? 'text-red-400' : 
                                      c.variance < 0 ? 'text-yellow-400' : 'text-slate-400'
                                    }`}>
                                      {c.variance > 0 ? '+' : ''}{c.variance.toFixed(1)} hrs
                                    </span>
                                  </td>
                                  <td className="py-2 text-right">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedClient({ clientName: c.clientName, week: c.week, revenue: c.revenue });
                                      }}
                                      className="text-green-400 font-medium hover:underline"
                                    >
                                      {formatCurrency(c.revenue)}
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {clients.length === 0 && (
        <div className="card p-8 text-center text-slate-400">
          No client data available
        </div>
      )}

      {/* Revenue Drill-down Modal - using Portal to render at body level */}
      {selectedClient && createPortal(
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4"
          style={{ zIndex: 9999 }}
          onClick={() => setSelectedClient(null)}
        >
          <div 
            className="bg-slate-800 rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-slate-700 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white">{selectedClient.clientName}</h3>
                <p className="text-sm text-slate-400">{selectedClient.week}</p>
              </div>
              <button
                onClick={() => setSelectedClient(null)}
                className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-400" />
                <span className="text-sm text-slate-400">Total Revenue:</span>
                <span className="text-xl font-bold text-green-400">{formatCurrency(selectedClient.revenue)}</span>
              </div>
            </div>

            <h4 className="text-sm font-medium text-slate-300 mb-3">Services Breakdown</h4>
            <div className="space-y-2">
              {getClientServices(selectedClient.clientName, selectedClient.week).map((s, idx) => {
                const serviceDate = s.serviceDate ? new Date(s.serviceDate) : null;
                const formattedDate = serviceDate 
                  ? serviceDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })
                  : s.dayOfService || 'No Date';
                
                return (
                  <div key={idx} className="bg-slate-700/50 rounded-lg p-3 border border-slate-600">
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-200">{s.staffName || 'Unknown Staff'}</p>
                        <div className="flex items-center gap-4 text-xs text-slate-400 mt-1">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3 flex-shrink-0" />
                            <span>{formattedDate}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3 flex-shrink-0" />
                            <span>{s.serviceLength?.toFixed(1) || 0} hrs</span>
                          </div>
                        </div>
                      </div>
                      <span className="text-green-400 font-medium whitespace-nowrap">{formatCurrency(s.amountPaid || 0)}</span>
                    </div>
                  </div>
                );
              })}
              {getClientServices(selectedClient.clientName, selectedClient.week).length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">No paid services found for this period</p>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
